
<template>
  <nav class="wynelover-topnav">

    <!-- Language selector: desktop and mobile -->
    <div class="wynelover-topnav-language">
      <b-dropdown
        split
        text="language"
        class="language-dropdown"
        toggle-class="toggle-language"
        split-class="split-language"
      >
        <template #button-content>
          <img
            :src="currentLanguageFlag"
            width="30px"
            height="20px"
            alt="Language"
          >
        </template>

        <b-dropdown-item
          v-for="language in languages"
          :key="language.code"
          @click="selectLanguage(language.code)"
        >
          <img
            :src="language.flag"
            width="30px"
            height="20px"
            :alt="language.name"
          >
          {{ language.name }}
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <!-- Desktop navigation -->
    <div class="wynelover-topnav-links">
      <a
        v-for="app in applications"
        :key="app.id"
        :href="app.url + '?lang=' + currentLanguage"
        :class="{ 'wynelover-topnav-active': activeApp === app.id }"
      >
        {{ app.name }}
      </a>
    </div>

    <!-- Mobile-only branding -->
    <div class="wynelover-mobile-brand">
      <span>WYNE</span>{{ currentAppSuffix }}
    </div>

    <!-- Mobile horizontal navigation strip: always visible -->
    <div class="wynelover-mobile-links">
      <a
        v-for="app in applications"
        :key="app.id"
        :href="app.url + '?lang=' + currentLanguage"
        :class="{ 'wynelover-topnav-active': activeApp === app.id }"
      >
        {{ app.name }}
      </a>
    </div>

  </nav>
</template>

<script>
import Cookies from 'js-cookie';

const flags = {
  en: require('@/assets/img/flags/english.png'),
  fr: require('@/assets/img/flags/francais.png'),
  de: require('@/assets/img/flags/deutsch.png'),
  es: require('@/assets/img/flags/spanish.png'),
  it: require('@/assets/img/flags/italian.png'),
  nl: require('@/assets/img/flags/dutch.png'),
  pt: require('@/assets/img/flags/portuguese.png')
};

export default {
  name: 'WyneLover_TopNav',

  props: {
    activeApp: {
      type: String,
      default: 'lover'
    }
  },

  created() {
    const urlLanguage = new URLSearchParams(window.location.search).get('lang');
    const cookieLanguage = Cookies.get('wynelover.language');

    const language = [urlLanguage, cookieLanguage].find(
      code => Object.prototype.hasOwnProperty.call(flags, code)
    );

    if (language) {
      this.$store.commit('setLanguage', language);
      Cookies.set('wynelover.language', language, {
        expires: 365,
        path: '/',
        sameSite: 'Lax'
      });
    }
  },

  computed: {
    applications() {
      return [
        {
          id: 'lover',
          name: 'WYNELOVER',
          url: 'https://www.wynelover.com'
        },
        {
          id: 'cellar',
          name: 'WYNECELLAR',
          url: 'https://www.wynecellar.com'
        },
        {
          id: 'taster',
          name: 'WYNETASTER',
          url: 'https://www.wynetaster.com'
        },
        {
          id: 'maker',
          name: 'WYNEMAKER',
          url: 'https://www.wynemaker.com'
        },
        {
          id: 'atlas',
          name: 'WYNEATLAS',
          url: 'https://www.wynelover.com/atlas'
        }
      ];
    },

    currentAppSuffix() {
      const app = this.applications.find(
        app => app.id === this.activeApp
      );

      if (!app) {
        return 'LOVER';
      }

      return app.name.replace(/^WYNE/, '');
    },

    currentLanguage() {
      return this.$store.state.language;
    },

    currentLanguageFlag() {
      return flags[this.currentLanguage] || flags.en;
    },

    languages() {
      return [
        { code: 'en', name: 'English', flag: flags.en },
        { code: 'fr', name: 'Français', flag: flags.fr },
        { code: 'de', name: 'Deutsch', flag: flags.de },
        { code: 'es', name: 'Español', flag: flags.es },
        { code: 'it', name: 'Italiano', flag: flags.it },
        { code: 'nl', name: 'Nederlands', flag: flags.nl },
        { code: 'pt', name: 'Português', flag: flags.pt }
      ];
    }
  },

  methods: {
    selectLanguage(language) {
      if (!Object.prototype.hasOwnProperty.call(flags, language)) {
        language = 'en';
      }

      this.$store.commit('setLanguage', language);

      Cookies.set('wynelover.language', language, {
        expires: 365,
        path: '/',
        sameSite: 'Lax'
      });
    }
  }
};
</script>

<style scoped>
/* =========================================
   TOP NAVIGATION
   ========================================= */

.wynelover-topnav {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 58px;
  background-color: white;
  border-bottom: 1px solid rgb(230,230,230);
}

/* =========================================
   DESKTOP LINKS
   ========================================= */

.wynelover-topnav-links {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
  gap: 32px;
  padding: 15px 20px;
}

.wynelover-topnav-links a {
  color: rgb(112,112,112);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.2s;
}

.wynelover-topnav-links a:hover {
  color: rgb(109,122,82);
  text-decoration: none;
}

.wynelover-topnav-links .wynelover-topnav-active {
  color: rgb(109,122,82);
  border-bottom: 3px solid rgb(109,122,82);
  padding-bottom: 5px;
}

/* =========================================
   LANGUAGE SELECTOR
   ========================================= */

.wynelover-topnav-language {
  position: absolute;
  left: 25px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  z-index: 110;
}

/* =========================================
   MOBILE ELEMENTS: HIDDEN ON DESKTOP
   ========================================= */

.wynelover-mobile-brand,
.wynelover-mobile-links {
  display: none;
}

/* =========================================
   MOBILE
   ========================================= */

@media (max-width: 768px) {
  .wynelover-topnav {
    min-height: 58px;
    padding: 0;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
  }

  .wynelover-topnav-links { display: none; }

  /* Keep the flag fixed to the first, 58px-high row. */
  .wynelover-topnav-language {
    left: 10px;
    top: 29px;
  }

  .wynelover-mobile-brand {
    display: flex;
    height: 58px;
    align-items: center;
    justify-content: center;
    color: #1a1a1a;
    font-size: clamp(15px, 4.3vw, 19px);
    font-weight: 600;
    white-space: nowrap;
  }

  .wynelover-mobile-brand span {
    color: #6d7a52;
    font-weight: 700;
  }

  /* Single-line application strip, always visible.
     Narrow phones can scroll the links horizontally. */
  .wynelover-mobile-links {
    position: static;
    display: flex;
    order: 2;
    flex: 0 0 100%;
    width: 100%;
    min-width: 0;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: flex-start;
    gap: clamp(2px, 1vw, 7px);
    padding: 8px clamp(4px, 1.2vw, 10px);
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    background: #ffffff;
    border-top: 1px solid #eeeeee;
    border-bottom: 1px solid #e6e6e6;
    box-shadow: 0 5px 12px rgba(0,0,0,.08);
    z-index: 100;
  }

  .wynelover-mobile-links::-webkit-scrollbar {
    display: none;
  }

  .wynelover-mobile-links a {
    display: inline-flex;
    flex: 0 0 auto;
    min-height: 32px;
    align-items: center;
    justify-content: center;
    padding: 6px clamp(2px, .75vw, 6px) 4px;
    border-bottom: 2px solid transparent;
    color: #707070;
    font-size: clamp(10px, 2.35vw, 12px);
    font-weight: 600;
    line-height: 1.2;
    text-decoration: none;
    white-space: nowrap;
  }

  /* Centre the strip when it fits; otherwise it scrolls from the left. */
  .wynelover-mobile-links a:first-child { margin-left: auto; }
  .wynelover-mobile-links a:last-child  { margin-right: auto; }

  .wynelover-mobile-links a:hover,
  .wynelover-mobile-links a:focus-visible {
    color: #6d7a52;
    text-decoration: none;
  }

  .wynelover-mobile-links .wynelover-topnav-active {
    color: #6d7a52;
    border-bottom: 2px solid #6d7a52;
  }
}
</style>